import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Menu,
  MenuItem,
  IconButton,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import useFetchWithAuth from "../../../features/hooks/useFetchWithAuth";

const getDefaultPeriod = (options) => {
  const day = new Date().getDate();
  const defaultRange =
    day >= 21 ? "21-31" : day >= 11 ? "11-20" : "1-10";
  if (options.includes(defaultRange)) return defaultRange;
  return options[0] || "";
};

const RatingTable = ({ periodOptions = [] }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selected, setSelected] = useState(() =>
    getDefaultPeriod(periodOptions)
  );
  const open = Boolean(anchorEl);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = (option) => {
    if (option) setSelected(option);
    setAnchorEl(null);
  };

  useEffect(() => {
    if (!selected) {
      setSelected(getDefaultPeriod(periodOptions));
    }
  }, [periodOptions, selected]);

  const formatDate = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const dateRange = useMemo(() => {
    if (typeof selected !== "string") return null;
    const match = selected.match(/^\s*(\d{1,2})\s*-\s*(\d{1,2})\s*$/);
    if (!match) return null;
    const startDay = parseInt(match[1], 10);
    const endDay = parseInt(match[2], 10);
    if (!Number.isFinite(startDay) || !Number.isFinite(endDay)) return null;
    const now = new Date();
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const from = new Date(now.getFullYear(), now.getMonth(), startDay);
    const to = new Date(
      now.getFullYear(),
      now.getMonth(),
      Math.min(endDay, lastDay)
    );
    return { from: formatDate(from), to: formatDate(to) };
  }, [selected]);

  const days = useMemo(() => {
    if (dateRange) return null;
    if (typeof selected !== "string") return 1;
    const parsed = parseInt(selected, 10);
    if (Number.isFinite(parsed)) return parsed;
    const lowered = selected.toLowerCase();
    if (lowered.includes("день") || lowered.includes("сьогодні")) return 1;
    return 1;
  }, [selected, dateRange]);

  const ratingsParams = new URLSearchParams();
  if (dateRange) {
    ratingsParams.set("date_from", dateRange.from);
    ratingsParams.set("date_to", dateRange.to);
  } else {
    ratingsParams.set("days", String(days ?? 1));
  }
  const ratingsEndpoint = `/statistic/operator/ratings/?${ratingsParams.toString()}`;

  const {
    data: allData,
    loading: loadingData,
    error: errorData,
  } = useFetchWithAuth(ratingsEndpoint);
  const {
    data: userData,
    loading: loadingUser,
    error: errorUser,
  } = useFetchWithAuth("/user/me/");

  const loading = loadingData || loadingUser;
  const error = errorData || errorUser;

  const rows = Array.isArray(allData?.data) ? allData.data : [];
  const normalizedRows = useMemo(() => {
    return rows.map((row) => ({
      ...row,
      full_name:
        (row.full_name ??
          `${row.first_name || ""} ${row.last_name || ""}`.trim()) ||
        row.username ||
        "-",
      avg_check: row.avg_check ?? row.average_check ?? 0,
      upsell_percent: row.upsell_percent ?? row.percent_upsell ?? 0,
      leads_per_hour:
        row.leads_per_hour ?? row.orders_per_hour ?? row.leadsPerHour ?? 0,
    }));
  }, [rows]);
  const filteredRows = useMemo(() => {
    return normalizedRows.filter((row) => {
      const values = [
        row.accepted_count,
        row.orders_without_upsell,
        row.cross_count,
        row.upsell_margin_sum,
        row.avg_check,
        row.upsell_percent,
        row.operator_salary ?? row.salary_amount ?? row.salary,
        row.combo_days,
      ];
      return values.some((value) => Number(value) || 0);
    });
  }, [normalizedRows]);
  const fullName =
    `${userData?.first_name || ""} ${userData?.last_name || ""}`.trim() ||
    userData?.username ||
    "";
  const [firstName, lastName] = fullName.split(" ");
  const userId = userData?.id ?? userData?.user_id ?? null;
  const isSameUser = (row) => {
    if (userId !== null && userId !== undefined && row?.id !== undefined) {
      return String(row.id) === String(userId);
    }
    if (!row?.full_name) return false;
    const rowParts = row.full_name.split(" ");
    return (
      rowParts[0]?.toLowerCase() === firstName.toLowerCase() &&
      rowParts[1]?.[0]?.toLowerCase() === lastName?.[0]?.toLowerCase()
    );
  };

  const userRow = normalizedRows.find(isSameUser) || null;
  const userIndex = userRow ? filteredRows.findIndex(isSameUser) : -1;
  const userPlace = userIndex !== -1 ? `${userIndex + 1} місце` : "Немає даних";
  const userStats = {
    acceptedCount: userRow?.accepted_count ?? 0,
    upsellMargin: userRow?.upsell_margin_sum ?? 0,
    upsellPercent: userRow?.upsell_percent ?? 0,
    avgCheck: userRow?.avg_check ?? 0,
  };
  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error.message}</p>;
  return (
    <Card
      sx={{
        background: "var(--whiteColor)",
        color: "var(--blackColor)",
        borderRadius: 4,
        p: 4,
        mb: 3,
        boxShadow: "none",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box>
          <Typography
            sx={{
              color: "var(--bodyColor)",
              fontWeight: 500,
              fontSize: 16,
              mb: 0.5,
            }}
          >
            Ваш рейтинг
          </Typography>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: "var(--blackColor)", fontSize: 26 }}
          >
            {userPlace}
          </Typography>
        </Box>

        {periodOptions.length > 0 && (
          <Box>
            <IconButton
              onClick={handleClick}
              sx={{
                color: "var(--blackColor)",
                borderRadius: 2,
                px: 2,
                py: 1,
                fontWeight: 500,
                fontSize: 16,
                border: "1px solid var(--borderColor)",
                minWidth: 120,
                ".dark-theme &": {
                  color: "var(--whiteColor)",
                  border: "1px solid var(--darkBorder)",
                },
              }}
            >
              {selected} <KeyboardArrowDownIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={() => handleClose()}
              sx={{ mt: 1 }}
            >
              {periodOptions.map((option) => (
                <MenuItem
                  key={option}
                  selected={option === selected}
                  onClick={() => handleClose(option)}
                >
                  {option}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 2,
        }}
      >
        {[
          { label: "Прийнятих", value: userStats.acceptedCount },
          { label: "Сума допродажу", value: userStats.upsellMargin },
          { label: "% допродажів", value: `${userStats.upsellPercent}%` },
          { label: "Середній чек", value: userStats.avgCheck },
        ].map((stat) => (
          <Box
            key={stat.label}
            sx={{
              border: "1px solid var(--borderColor)",
              borderRadius: 3,
              p: 2.5,
              background: "var(--whiteColor)",
              ".dark-theme &": {
                background: "var(--darkCardBg)",
                borderColor: "var(--darkBorder)",
              },
            }}
          >
            <Typography
              sx={{
                color: "var(--bodyColor)",
                fontWeight: 500,
                fontSize: 14,
                mb: 1,
              }}
            >
              {stat.label}
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 22,
                color: "var(--blackColor)",
                ".dark-theme &": { color: "var(--whiteColor)" },
              }}
            >
              {stat.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Card>
  );
};

export default RatingTable;
